import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Pagination,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import TicketService from "../services/TicketService";
import appConfig from "../shared/js/app-config";
import { bookingContactSearchHaystack, formatBookingContact } from "../shared/utils/bookingContactDisplay";
import { formatBookingLineSeats } from "../shared/utils/ticketBookingDisplay";

const emptyEvent = {
  id: null,
  kind: "event",
  parent_event_id: "",
  slug: "",
  title: "",
  venue_name: "",
  origin: "",
  destination: "",
  duration_label: "",
  itinerary_text: "",
  inclusions: "",
  vehicle_label: "",
  route_stops: [],
  route_segments: [],
  starts_at_utc: "",
  ends_at_utc: "",
  doors_open_at_utc: "",
  event_timezone: "Asia/Kolkata",
  status: "draft",
  shared_seat_pool: false,
  shared_seat_labels: "",
  travel_base_price_inr: "",
  travel_max_travellers: "",
  travel_hotels: [],
  travel_vehicles: [],
};

const emptyTier = {
  id: null,
  event_id: "",
  code: "",
  name: "",
  capacity: 0,
  unit_price_minor: 0,
  currency: "INR",
  seat_map_mode: "general_admission",
  seat_sections_json: "",
  seat_labels: "",
  seats_per_unit: 1,
};

const statusOptions = ["draft", "open", "postponed", "cancelled", "completed"];

const kindLabels = {
  event: "Event, class, or workshop",
  route: "Travel route or tour",
  package: "Tour package catalog",
  departure: "Package departure date",
};

function eventCatalogKind(row) {
  const meta = parseJson(row?.metadata_json, {});
  if (meta.kind === "package") return "package";
  if (row?.parent_event_id) return "departure";
  return meta.kind || "event";
}

const statusLabels = {
  draft: "Draft",
  open: "Open",
  postponed: "Postponed",
  cancelled: "Cancelled",
  completed: "Completed",
};

const seatModeLabels = {
  general_admission: "Total capacity only",
  sectioned: "Section-wise capacity",
  assigned_seat: "Customers choose exact seats",
};

const EVENT_PAGE_SIZE = 12;
const TIER_PAGE_SIZE = 12;

function friendlyKey(value, fallback = "ticket") {
  const key = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return key || fallback;
}

function formatMoney(currency, minor) {
  const amount = Number(minor || 0) / 100;
  return `${currency || "INR"} ${amount.toFixed(2)}`;
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function parseJson(raw, fallback = {}) {
  if (!raw) return fallback;
  if (typeof raw === "object") return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function toDatetimeLocal(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 16);
}

function toIsoOrNull(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function dayStart(value) {
  if (!value) return null;
  const d = new Date(`${value}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function dayEnd(value) {
  if (!value) return null;
  const d = new Date(`${value}T23:59:59`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function parseSeatLabels(value) {
  return String(value || "")
    .split(/[\n,]+/)
    .map((label) => label.trim())
    .filter(Boolean);
}

function seatLabelsFromSections(sections) {
  return asArray(sections).flatMap((section) => {
    if (Array.isArray(section?.seats)) return section.seats.map(String);
    const rows = asArray(section?.rows);
    const seatsPerRow = Number(section?.seats_per_row || 0);
    if (rows.length && seatsPerRow > 0) {
      return rows.flatMap((row) => Array.from({ length: seatsPerRow }, (_, idx) => `${row}${idx + 1}`));
    }
    return [];
  });
}

function buildSeatSections(mode, labelText) {
  if (mode !== "assigned_seat") return [];
  const labels = parseSeatLabels(labelText);
  return labels.length ? [{ section: "Seats", seats: labels }] : [];
}

function useNotice() {
  const [notice, setNotice] = useState({ open: false, message: "", severity: "success" });
  const show = (message, severity = "success") => setNotice({ open: true, message, severity });
  const node = (
    <Snackbar
      open={notice.open}
      autoHideDuration={3500}
      onClose={() => setNotice((n) => ({ ...n, open: false }))}
    >
      <Alert severity={notice.severity} onClose={() => setNotice((n) => ({ ...n, open: false }))}>
        {notice.message}
      </Alert>
    </Snackbar>
  );
  return { show, node };
}

function SectionCard({ title, subtitle, action, children, sx }) {
  return (
    <Card
      variant="outlined"
      sx={{
        width: "100%",
        borderRadius: 3,
        borderColor: "rgba(15,23,42,0.10)",
        boxShadow: "0 10px 28px rgba(15,23,42,0.04)",
        ...sx,
      }}
    >
      <CardContent sx={{ p: 2.25 }}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={2} mb={2}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 750 }}>{title}</Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          {action}
        </Stack>
        {children}
      </CardContent>
    </Card>
  );
}

function eventToForm(row) {
  const meta = parseJson(row.metadata_json, {});
  const eventSeatSections = meta.seat_map?.sections || [];
  const kind = row.parent_event_id ? "departure" : (meta.kind || "event");
  return {
    ...emptyEvent,
    ...row,
    kind,
    parent_event_id: row.parent_event_id ? String(row.parent_event_id) : "",
    destination: meta.destination || meta.route?.destination || "",
    duration_label: meta.duration_label || "",
    itinerary_text: meta.itinerary_text || "",
    inclusions: meta.inclusions || "",
    shared_seat_pool: meta.seat_map?.shared_pool === true,
    shared_seat_labels: seatLabelsFromSections(eventSeatSections).join(", "),
    origin: meta.route?.origin || "",
    vehicle_label: meta.route?.vehicle_label || "",
    route_stops: asArray(meta.route?.stops).map((stop) => ({
      ...stop,
      time: toDatetimeLocal(stop.time),
    })),
    route_segments: asArray(meta.route?.segments),
    starts_at_utc: toDatetimeLocal(row.starts_at_utc),
    ends_at_utc: toDatetimeLocal(row.ends_at_utc),
    doors_open_at_utc: toDatetimeLocal(row.doors_open_at_utc),
    travel_base_price_inr: meta.travel?.base_price_minor != null
      ? String(Number(meta.travel.base_price_minor) / 100)
      : "",
    travel_max_travellers: meta.travel?.capacity != null
      ? String(meta.travel.capacity)
      : "",
    travel_hotels: asArray(meta.travel?.optional_hotels).map((hotel) => ({
      name: hotel.name || "",
      price_inr: hotel.price_minor_per_person != null
        ? String(Number(hotel.price_minor_per_person) / 100)
        : "",
    })),
    travel_vehicles: asArray(meta.travel?.optional_vehicles).map((vehicle) => ({
      name: vehicle.name || "",
      price_inr: vehicle.price_minor_per_person != null
        ? String(Number(vehicle.price_minor_per_person) / 100)
        : "",
    })),
  };
}

function toMinorFromInr(value) {
  const n = Number(String(value || "").replace(/,/g, "").trim());
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.round(n * 100);
}

function tierToForm(row, eventId) {
  const meta = parseJson(row.metadata_json, {});
  const sections = meta.seat_map?.sections || [];
  return {
    ...emptyTier,
    ...row,
    event_id: String(eventId || row.event_id || ""),
    seat_map_mode: meta.seat_map?.mode || "general_admission",
    seat_sections_json: sections ? JSON.stringify(sections, null, 2) : "",
    seat_labels: seatLabelsFromSections(sections).join(", "),
    seats_per_unit: Number(meta.seat_map?.seats_per_unit ?? 1) || 1,
  };
}

export default function TicketOperationsView() {
  const serviceId = appConfig.getServiceId();
  const [tab, setTab] = useState("events");
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [tiersByEvent, setTiersByEvent] = useState({});
  const [bookings, setBookings] = useState([]);
  const [registry, setRegistry] = useState(null);
  const [eventForm, setEventForm] = useState(emptyEvent);
  const [selectedEventId, setSelectedEventId] = useState("");
  const [tierForm, setTierForm] = useState(emptyTier);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [tierDialogOpen, setTierDialogOpen] = useState(false);
  const [eventFilters, setEventFilters] = useState({
    search: "",
    kind: "",
    status: "",
    created_from: "",
    created_to: "",
  });
  const [tierFilters, setTierFilters] = useState({ search: "", seat_mode: "" });
  const [eventPage, setEventPage] = useState(1);
  const [tierPage, setTierPage] = useState(1);
  const [availability, setAvailability] = useState(null);
  const [chatbotPreview, setChatbotPreview] = useState({
    event_id: "",
    lines: [{ tier_id: "", qty: 1 }],
    passengers_json: "",
  });
  const [showDeveloperChatbotRef, setShowDeveloperChatbotRef] = useState(false);
  const [bookingFilters, setBookingFilters] = useState({ event_id: "", status: "", contact: "" });
  const [lineEdit, setLineEdit] = useState({ booking_id: null, line_id: null, new_qty: "" });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const { show, node: noticeNode } = useNotice();

  const selectedEvent = useMemo(
    () => events.find((e) => String(e.id) === String(selectedEventId)),
    [events, selectedEventId]
  );

  const selectedTiers = asArray(tiersByEvent[String(selectedEventId)]);

  const filteredEvents = useMemo(() => {
    const search = eventFilters.search.trim().toLowerCase();
    const fromDate = dayStart(eventFilters.created_from);
    const toDate = dayEnd(eventFilters.created_to);
    return events.filter((event) => {
      const meta = parseJson(event.metadata_json, {});
      const kind = eventCatalogKind(event);
      if (eventFilters.kind && kind !== eventFilters.kind) return false;
      if (eventFilters.status && event.status !== eventFilters.status) return false;
      const createdAt = event.created_at ? new Date(event.created_at) : null;
      if (fromDate && createdAt && createdAt < fromDate) return false;
      if (toDate && createdAt && createdAt > toDate) return false;
      if (search) {
        const haystack = [
          event.title,
          event.slug,
          event.venue_name,
          meta.route?.origin,
          meta.route?.destination,
          meta.destination,
          meta.duration_label,
          meta.inclusions,
          meta.route?.vehicle_label,
          ...asArray(meta.route?.stops).map((stop) => stop.name),
        ].filter(Boolean).join(" ").toLowerCase();
        if (!haystack.includes(search)) return false;
      }
      return true;
    });
  }, [events, eventFilters]);

  const eventPageCount = Math.max(1, Math.ceil(filteredEvents.length / EVENT_PAGE_SIZE));
  const currentEventPage = Math.min(eventPage, eventPageCount);
  const visibleEvents = filteredEvents.slice(
    (currentEventPage - 1) * EVENT_PAGE_SIZE,
    currentEventPage * EVENT_PAGE_SIZE
  );

  const filteredTiers = useMemo(() => {
    const search = tierFilters.search.trim().toLowerCase();
    return selectedTiers.filter((tier) => {
      const meta = parseJson(tier.metadata_json, {});
      const seatMode = meta.seat_map?.mode || "general_admission";
      if (tierFilters.seat_mode && seatMode !== tierFilters.seat_mode) return false;
      if (search) {
        const haystack = [
          tier.name,
          tier.code,
          tier.currency,
          seatModeLabels[seatMode],
        ].filter(Boolean).join(" ").toLowerCase();
        if (!haystack.includes(search)) return false;
      }
      return true;
    });
  }, [selectedTiers, tierFilters]);

  const tierPageCount = Math.max(1, Math.ceil(filteredTiers.length / TIER_PAGE_SIZE));
  const currentTierPage = Math.min(tierPage, tierPageCount);
  const visibleTiers = filteredTiers.slice(
    (currentTierPage - 1) * TIER_PAGE_SIZE,
    currentTierPage * TIER_PAGE_SIZE
  );

  const loadAll = async () => {
    if (!serviceId) return;
    setLoading(true);
    try {
      const [nextEvents, nextBookings, nextRegistry] = await Promise.all([
        TicketService.listEvents(serviceId),
        TicketService.listBookings(serviceId),
        TicketService.actionRegistry().catch(() => null),
      ]);
      const evs = asArray(nextEvents);
      setEvents(evs);
      setBookings(asArray(nextBookings));
      setRegistry(nextRegistry);
      if (!selectedEventId && evs[0]?.id) setSelectedEventId(String(evs[0].id));
      const pairs = await Promise.all(
        evs.map(async (event) => [String(event.id), await TicketService.listTiers(event.id, serviceId).catch(() => [])])
      );
      setTiersByEvent(Object.fromEntries(pairs.map(([id, tiers]) => [id, asArray(tiers)])));
    } catch (e) {
      show("Could not load ticket data", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId]);

  useEffect(() => {
    if (!selectedEvent) return;
    setTierForm((prev) => ({ ...prev, event_id: String(selectedEvent.id) }));
  }, [selectedEvent]);

  useEffect(() => {
    setEventPage(1);
  }, [eventFilters]);

  useEffect(() => {
    setTierPage(1);
  }, [tierFilters, selectedEventId]);

  const packageEvents = useMemo(
    () => events.filter((event) => eventCatalogKind(event) === "package"),
    [events]
  );

  const buildEventPayload = () => {
    const kind = eventForm.kind || "event";
    const title = eventForm.title.trim();
    const prevMeta = parseJson(eventForm.metadata_json, {});
    const seat_map = { ...(prevMeta.seat_map || {}) };
    if (eventForm.shared_seat_pool && kind !== "package") {
      seat_map.shared_pool = true;
      seat_map.sections = buildSeatSections("assigned_seat", eventForm.shared_seat_labels);
    } else if (kind === "package") {
      seat_map.shared_pool = false;
    } else {
      seat_map.shared_pool = false;
    }
    const metadata_json = {
      ...prevMeta,
      kind: kind === "departure" ? "departure" : kind,
      destination: kind === "package" ? (eventForm.destination || null) : prevMeta.destination,
      duration_label: kind === "departure"
        ? (eventForm.duration_label || null)
        : (kind === "package" ? null : prevMeta.duration_label),
      itinerary_text: kind === "departure" ? (eventForm.itinerary_text || null) : prevMeta.itinerary_text,
      inclusions: kind === "package" ? (eventForm.inclusions || null) : prevMeta.inclusions,
      seat_map,
      route: kind === "route"
        ? {
          origin: eventForm.origin,
          destination: eventForm.destination,
          vehicle_label: eventForm.vehicle_label,
          stops: asArray(eventForm.route_stops)
            .filter((stop) => stop.name || stop.time)
            .map((stop) => ({ ...stop, time: toIsoOrNull(stop.time) || stop.time || null })),
          segments: asArray(eventForm.route_segments)
            .filter((segment) => segment.origin || segment.destination)
            .map((segment) => ({
              origin: segment.origin || null,
              destination: segment.destination || null,
              capacity: Number(segment.capacity || 0),
            })),
        }
        : null,
    };
    const startsAt = toIsoOrNull(eventForm.starts_at_utc)
      || (kind === "package" ? new Date().toISOString() : null);
    const parentEventId = kind === "departure" && eventForm.parent_event_id
      ? Number(eventForm.parent_event_id)
      : null;
    const travel_addons = kind === "departure"
      ? {
        base_price_minor: toMinorFromInr(eventForm.travel_base_price_inr),
        capacity: (() => {
          const n = Number(String(eventForm.travel_max_travellers || "").trim());
          return Number.isFinite(n) && n > 0 ? Math.max(1, Math.floor(n)) : undefined;
        })(),
        hotels: asArray(eventForm.travel_hotels)
          .filter((row) => row.name && row.price_inr !== "")
          .map((row, index) => ({
            id: `hotel_${index + 1}`,
            name: row.name.trim(),
            price_minor_per_person: toMinorFromInr(row.price_inr),
          })),
        vehicles: asArray(eventForm.travel_vehicles)
          .filter((row) => row.name && row.price_inr !== "")
          .map((row, index) => ({
            id: `vehicle_${index + 1}`,
            name: row.name.trim(),
            price_minor_per_person: toMinorFromInr(row.price_inr),
          })),
      }
      : undefined;

    return {
      slug: eventForm.slug.trim() || friendlyKey(title, kind),
      title,
      parent_event_id: parentEventId,
      travel_addons,
      venue_name: kind === "package" ? null : (eventForm.venue_name || null),
      address_json: null,
      starts_at_utc: startsAt,
      ends_at_utc: toIsoOrNull(eventForm.ends_at_utc),
      doors_open_at_utc: toIsoOrNull(eventForm.doors_open_at_utc),
      event_timezone: eventForm.event_timezone || "Asia/Kolkata",
      status: eventForm.status || "draft",
      metadata_json,
    };
  };

  const saveEvent = async () => {
    const payload = buildEventPayload();
    const kind = eventForm.kind || "event";
    if (!payload.title) {
      show("Name is required", "error");
      return;
    }
    if (kind === "departure" && !payload.parent_event_id) {
      show("Select the parent tour package for this departure", "error");
      return;
    }
    if (kind !== "package" && !payload.starts_at_utc) {
      show("Start date/time is required", "error");
      return;
    }
    if (kind === "departure") {
      const hasTravelPricing = String(eventForm.travel_base_price_inr || "").trim() !== ""
        || asArray(eventForm.travel_hotels).some((row) => String(row?.name || "").trim())
        || asArray(eventForm.travel_vehicles).some((row) => String(row?.name || "").trim());
      if (hasTravelPricing) {
        const cap = Number(String(eventForm.travel_max_travellers || "").trim());
        if (!Number.isFinite(cap) || cap < 1) {
          show("Enter how many travellers can book this date (shown as “X slots left” in WhatsApp)", "error");
          return;
        }
      }
    }
    try {
      const saved = eventForm.id
        ? await TicketService.updateEvent(eventForm.id, payload, serviceId)
        : await TicketService.createEvent(payload, serviceId);
      show(eventForm.id ? "Ticket inventory updated" : "Ticket inventory created");
      setEventForm(emptyEvent);
      setEventDialogOpen(false);
      if (saved?.id) setSelectedEventId(String(saved.id));
      loadAll();
    } catch (e) {
      show("Could not save ticket inventory", "error");
    }
  };

  const saveTier = async () => {
    const eventId = tierForm.event_id || selectedEventId;
    const tierName = tierForm.name.trim();
    if (!eventId || !tierName) {
      show("Select an event/route and enter ticket type name", "error");
      return;
    }
    const payload = {
      code: tierForm.code.trim() || friendlyKey(tierName, "ticket").toUpperCase(),
      name: tierName,
      capacity: Number(tierForm.capacity || 0),
      unit_price_minor: Number(tierForm.unit_price_minor || 0),
      currency: tierForm.currency || "INR",
      metadata_json: {
        ...parseJson(tierForm.metadata_json, {}),
        seat_map: {
          mode: tierForm.seat_map_mode || "general_admission",
          sections: buildSeatSections(tierForm.seat_map_mode, tierForm.seat_labels),
          seats_per_unit: Math.max(1, Number(tierForm.seats_per_unit || 1)),
        },
      },
    };
    try {
      if (tierForm.id) {
        await TicketService.updateTier(eventId, tierForm.id, payload, serviceId);
        show("Tier updated");
      } else {
        await TicketService.createTier(eventId, payload, serviceId);
        show("Tier created");
      }
      setTierForm({ ...emptyTier, event_id: String(eventId) });
      setTierDialogOpen(false);
      loadAll();
    } catch (e) {
      show("Could not save tier", "error");
    }
  };

  const archiveEvent = async (event, status) => {
    try {
      await TicketService.updateEvent(event.id, { ...event, status }, serviceId);
      show(`Inventory marked ${status}`);
      loadAll();
    } catch (e) {
      show("Could not update status", "error");
    }
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const openDeleteEventDialog = (event) => {
    setDeleteTarget({ type: "event", event });
    setDeleteDialogOpen(true);
  };

  const openDeleteTierDialog = (tier) => {
    setDeleteTarget({ type: "tier", tier });
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      if (deleteTarget.type === "event" && deleteTarget.event) {
        const { event } = deleteTarget;
        await TicketService.deleteEvent(event.id, serviceId);
        if (String(selectedEventId) === String(event.id)) {
          setSelectedEventId("");
        }
        show("Deleted");
      } else if (deleteTarget.type === "tier" && deleteTarget.tier) {
        const { tier } = deleteTarget;
        const eventId = tier.event_id || selectedEventId;
        await TicketService.deleteTier(eventId, tier.id, serviceId);
        show("Ticket type deleted");
      }
      closeDeleteDialog();
      loadAll();
    } catch (e) {
      show(e?.response?.data?.message || e?.message || "Could not delete", "error");
    } finally {
      setDeleteLoading(false);
    }
  };

  const deleteDialogTitle = deleteTarget?.type === "tier"
    ? "Delete ticket type"
    : eventCatalogKind(deleteTarget?.event) === "package"
      ? "Delete package"
      : "Delete trip / event";

  const deleteDialogBody = deleteTarget?.type === "tier" ? (
    <>
      Are you sure you want to delete ticket type{" "}
      <strong>{deleteTarget.tier?.name || "this ticket type"}</strong>?
      This cannot be undone.
    </>
  ) : eventCatalogKind(deleteTarget?.event) === "package" ? (
    <>
      Are you sure you want to delete package{" "}
      <strong>{deleteTarget.event?.title || "this package"}</strong> and all its departures?
      This cannot be undone.
    </>
  ) : (
    <>
      Are you sure you want to delete{" "}
      <strong>{deleteTarget?.event?.title || "this trip/event"}</strong>?
      This cannot be undone.
    </>
  );

  const cancelBooking = async (booking) => {
    try {
      await TicketService.cancelBooking(booking.id, { reason: "merchant_cancelled", byMerchant: true }, serviceId);
      show("Booking cancelled");
      loadAll();
    } catch (e) {
      show("Could not cancel booking", "error");
    }
  };

  const reduceBookingLine = async () => {
    if (!lineEdit.booking_id || !lineEdit.line_id) return;
    try {
      await TicketService.reduceLine(
        lineEdit.booking_id,
        { line_id: lineEdit.line_id, new_qty: Number(lineEdit.new_qty || 0) },
        serviceId
      );
      setLineEdit({ booking_id: null, line_id: null, new_qty: "" });
      show("Booking line updated");
      loadAll();
    } catch (e) {
      show("Could not update booking line", "error");
    }
  };

  const checkTierAvailability = async (tierId) => {
    try {
      const data = await TicketService.tierAvailability(tierId, serviceId);
      setAvailability(data);
      show("Availability refreshed");
    } catch (e) {
      show("Could not fetch availability", "error");
    }
  };

  const updateRouteStop = (idx, patch) => {
    const next = [...asArray(eventForm.route_stops)];
    next[idx] = { ...(next[idx] || {}), ...patch };
    setEventForm({ ...eventForm, route_stops: next });
  };

  const updateRouteSegment = (idx, patch) => {
    const next = [...asArray(eventForm.route_segments)];
    next[idx] = { ...(next[idx] || {}), ...patch };
    setEventForm({ ...eventForm, route_segments: next });
  };

  const allTiers = Object.entries(tiersByEvent).flatMap(([eventId, tiers]) =>
    asArray(tiers).map((tier) => ({ ...tier, event_id: eventId }))
  );
  const tierNameById = Object.fromEntries(
    allTiers.map((tier) => [String(tier.id), `${tier.name} (${tier.code})`])
  );
  const filteredBookings = bookings.filter((booking) => {
    if (bookingFilters.event_id && String(booking.event_id) !== String(bookingFilters.event_id)) return false;
    if (bookingFilters.status && String(booking.status) !== String(bookingFilters.status)) return false;
    if (bookingFilters.contact) {
      const needle = bookingFilters.contact.toLowerCase();
      if (!bookingContactSearchHaystack(booking).includes(needle)) return false;
    }
    return true;
  });

  const exportBookingsCsv = () => {
    const rows = filteredBookings.flatMap((booking) => {
      const event = events.find((e) => Number(e.id) === Number(booking.event_id));
      const lines = asArray(booking.lines);
      return (lines.length ? lines : [{}]).map((line) => ({
        booking_id: booking.id,
        contact_id: booking.contact_id,
        contact_name: booking.contact_name || "",
        contact_phone: booking.contact_phone || "",
        event: event?.title || booking.event_id,
        status: booking.status,
        total_minor: booking.total_minor,
        currency: booking.currency,
        tier: line.tier_name || tierNameById[String(line.ticket_tier_id)] || "",
        qty: line.qty || "",
        passengers: asArray(parseJson(booking.passenger_manifest_json, [])).map((p) => p.name || p.full_name || "").filter(Boolean).join(" | "),
      }));
    });
    const header = ["booking_id", "contact_name", "contact_phone", "contact_id", "event", "status", "total_minor", "currency", "tier", "qty", "passengers"];
    const csv = [
      header.join(","),
      ...rows.map((row) => header.map((key) => `"${String(row[key] ?? "").replace(/"/g, '""')}"`).join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "ticket-bookings.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const chatbotLines = asArray(chatbotPreview.lines)
    .filter((line) => line.tier_id)
    .map((line) => ({
      ticket_tier_id: Number(line.tier_id),
      qty: Number(line.qty || 1),
      ...(String(line.seats || "").trim()
        ? {
          seat_section_json: {
            seats: String(line.seats).split(",").map((s) => s.trim()).filter(Boolean),
            ...(line.segment ? { segment: line.segment } : {}),
          },
        }
        : line.segment
          ? { seat_section_json: { segment: line.segment } }
        : {}),
    }));
  const holdPayloadPreview = {
    contact_id: "{{contact_id}}",
    lines: chatbotLines.length ? chatbotLines : [{ ticket_tier_id: "<ticket_tier_id>", qty: 1 }],
    passengers: parseJson(chatbotPreview.passengers_json, []),
    ttl_minutes: 15,
    idempotency_key: "{{conversation_id}}:ticket_hold",
    metadata_json: {
      selected_event_id: chatbotPreview.event_id || null,
    },
  };

  const registryActions = asArray(registry?.actions).filter((a) =>
    String(a.id || "").startsWith("ticket.") || a.id === "checkout.send_native_review_pay"
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1440, width: "100%", mx: "auto" }}>
      <Box
        sx={{
          mb: 2,
          p: { xs: 2, md: 2.5 },
          borderRadius: 3,
          border: "1px solid rgba(15,23,42,0.08)",
          background: "linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)",
          boxShadow: "0 12px 30px rgba(15,23,42,0.05)",
        }}
      >
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 850, letterSpacing: "-0.02em" }}>
              Tickets & Reservations
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
              Create trips, events, ticket types, seat capacity, and booking records for WhatsApp flows.
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            onClick={loadAll}
            disabled={loading}
            sx={{ alignSelf: { xs: "flex-start", md: "center" }, borderRadius: 2, px: 2 }}
          >
            Refresh
          </Button>
        </Stack>
      </Box>

      <Box sx={{ borderBottom: "1px solid rgba(15,23,42,0.10)", mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab value="events" label="Events / Routes" />
          <Tab value="tiers" label="Ticket types & seats" />
          <Tab value="bookings" label="Bookings" />
          <Tab value="chatbot" label="Chatbot data" />
        </Tabs>
      </Box>

      {tab === "events" && (
        <SectionCard
          title="Saved trips and events"
          subtitle="Select one to edit or manage ticket types. Long lists stay inside this panel."
          action={(
            <Button
              variant="contained"
              onClick={() => {
                setEventForm(emptyEvent);
                setEventDialogOpen(true);
              }}
              sx={{ borderRadius: 2, whiteSpace: "nowrap" }}
            >
              New trip / event
            </Button>
          )}
          sx={{ minHeight: 360 }}
        >
          <Stack direction={{ xs: "column", lg: "row" }} spacing={1.25} sx={{ mb: 2 }}>
            <TextField
              label="Search event, route, place"
              size="small"
              value={eventFilters.search}
              onChange={(e) => setEventFilters({ ...eventFilters, search: e.target.value })}
              sx={{ minWidth: { xs: "100%", lg: 280 } }}
            />
            <TextField
              select
              label="Type"
              size="small"
              value={eventFilters.kind}
              onChange={(e) => setEventFilters({ ...eventFilters, kind: e.target.value })}
              sx={{ minWidth: { xs: "100%", lg: 170 } }}
            >
              <MenuItem value="">All types</MenuItem>
              <MenuItem value="event">Events</MenuItem>
              <MenuItem value="route">Routes</MenuItem>
              <MenuItem value="package">Tour packages</MenuItem>
              <MenuItem value="departure">Departures</MenuItem>
            </TextField>
            <TextField
              select
              label="Status"
              size="small"
              value={eventFilters.status}
              onChange={(e) => setEventFilters({ ...eventFilters, status: e.target.value })}
              sx={{ minWidth: { xs: "100%", lg: 190 } }}
            >
              <MenuItem value="">All statuses</MenuItem>
              {statusOptions.map((status) => (
                <MenuItem key={status} value={status}>{statusLabels[status] || status}</MenuItem>
              ))}
            </TextField>
            <TextField
              type="date"
              label="Created from"
              size="small"
              value={eventFilters.created_from}
              onChange={(e) => setEventFilters({ ...eventFilters, created_from: e.target.value })}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: { xs: "100%", lg: 170 } }}
            />
            <TextField
              type="date"
              label="Created to"
              size="small"
              value={eventFilters.created_to}
              onChange={(e) => setEventFilters({ ...eventFilters, created_to: e.target.value })}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: { xs: "100%", lg: 170 } }}
            />
            <Button
              variant="text"
              onClick={() => setEventFilters({ search: "", kind: "", status: "", created_from: "", created_to: "" })}
              sx={{ alignSelf: { lg: "center" } }}
            >
              Clear
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Showing {visibleEvents.length} of {filteredEvents.length} filtered records ({events.length} total)
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(auto-fill, minmax(320px, 380px))",
              },
              justifyContent: "start",
              alignItems: "stretch",
              gap: 1.5,
              maxHeight: 620,
              overflowY: "auto",
              pr: 0.5,
            }}
          >
                {visibleEvents.map((event) => {
                  const meta = parseJson(event.metadata_json, {});
                  const catalogKind = eventCatalogKind(event);
                  const selected = String(event.id) === String(selectedEventId);
                  const parentPackage = catalogKind === "departure"
                    ? events.find((e) => String(e.id) === String(event.parent_event_id))
                    : null;
                  const detail = catalogKind === "route"
                    ? `${meta.route?.origin || "-"} → ${meta.route?.destination || "-"}${asArray(meta.route?.stops).length ? ` (${asArray(meta.route?.stops).length} stops)` : ""}${asArray(meta.route?.segments).length ? ` • ${asArray(meta.route?.segments).length} seat limits` : ""}`
                    : catalogKind === "package"
                      ? meta.destination || "Tour package catalog"
                      : catalogKind === "departure"
                        ? [
                            parentPackage?.title ? `Under: ${parentPackage.title}` : null,
                            event.starts_at_utc ? new Date(event.starts_at_utc).toLocaleString() : "Date not set",
                            meta.duration_label,
                          ].filter(Boolean).join(" • ")
                        : event.venue_name || "Venue not set";
                  const kindChip = catalogKind === "route"
                    ? "Route"
                    : catalogKind === "package"
                      ? "Package"
                      : catalogKind === "departure"
                        ? "Departure"
                        : "Event";
                  return (
                    <Box
                      key={event.id}
                      onClick={() => setSelectedEventId(String(event.id))}
                      sx={{
                        p: 1.75,
                        borderRadius: 2.5,
                        border: "1px solid",
                        borderColor: selected ? "primary.main" : "rgba(15,23,42,0.10)",
                        bgcolor: selected ? "rgba(25,118,210,0.04)" : "#fff",
                        boxShadow: selected ? "0 10px 24px rgba(25,118,210,0.10)" : "none",
                        cursor: "pointer",
                        minHeight: 148,
                        transition: "0.16s ease",
                        "&:hover": {
                          borderColor: selected ? "primary.main" : "rgba(25,118,210,0.35)",
                          boxShadow: "0 10px 24px rgba(15,23,42,0.08)",
                        },
                      }}
                    >
                      <Stack justifyContent="space-between" gap={1.5} sx={{ height: "100%" }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }} useFlexGap flexWrap="wrap">
                            <Chip size="small" label={kindChip} />
                            {catalogKind !== "package" ? (
                              <Chip size="small" label={statusLabels[event.status] || event.status} />
                            ) : null}
                          </Stack>
                          <Typography sx={{ fontWeight: 800, color: "#111827" }}>{event.title}</Typography>
                          <Typography variant="body2" color="text.secondary">{detail}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {event.starts_at_utc ? new Date(event.starts_at_utc).toLocaleString() : "Start time not set"}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="flex-end" flexWrap="wrap">
                          {catalogKind !== "package" ? (
                            <Button size="small" variant="contained" onClick={(e) => { e.stopPropagation(); setSelectedEventId(String(event.id)); setTab("tiers"); }}>
                              Ticket types
                            </Button>
                          ) : (
                            <Button
                              size="small"
                              variant="contained"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEventForm({ ...emptyEvent, kind: "departure", parent_event_id: String(event.id) });
                                setEventDialogOpen(true);
                              }}
                            >
                              Add departure
                            </Button>
                          )}
                          <Button size="small" onClick={(e) => { e.stopPropagation(); setEventForm(eventToForm(event)); setEventDialogOpen(true); }}>
                            Edit
                          </Button>
                          {event.status !== "cancelled" && (
                            <Button size="small" color="warning" onClick={(e) => { e.stopPropagation(); archiveEvent(event, "cancelled"); }}>
                              Cancel
                            </Button>
                          )}
                          {event.status === "cancelled" && (
                            <Button size="small" color="success" onClick={(e) => { e.stopPropagation(); archiveEvent(event, "draft"); }}>
                              Restore
                            </Button>
                          )}
                          <Button
                            size="small"
                            color="error"
                            onClick={(e) => { e.stopPropagation(); openDeleteEventDialog(event); }}
                          >
                            Delete
                          </Button>
                        </Stack>
                      </Stack>
                    </Box>
                  );
                })}
                {!visibleEvents.length && (
                  <Box sx={{ p: 3, textAlign: "center", border: "1px dashed rgba(15,23,42,0.18)", borderRadius: 2.5 }}>
                    <Typography sx={{ fontWeight: 700 }}>No matching trips or events</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Adjust filters or use New trip / event to create a booking item.
                    </Typography>
                  </Box>
                )}
          </Box>
          {eventPageCount > 1 && (
            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
              <Pagination
                count={eventPageCount}
                page={currentEventPage}
                onChange={(_, page) => setEventPage(page)}
                color="primary"
              />
            </Stack>
          )}
        </SectionCard>
      )}

      <Dialog open={eventDialogOpen} onClose={() => setEventDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {eventForm.id ? "Edit trip or event" : "Create trip or event"}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              select
              label="What are you setting up?"
              value={eventForm.kind}
              onChange={(e) => setEventForm({ ...eventForm, kind: e.target.value })}
              helperText="Choose route for buses, tours, or point-to-point travel."
            >
              <MenuItem value="event">{kindLabels.event}</MenuItem>
              <MenuItem value="route">{kindLabels.route}</MenuItem>
              <MenuItem value="package">{kindLabels.package}</MenuItem>
              <MenuItem value="departure">{kindLabels.departure}</MenuItem>
            </TextField>
            {eventForm.kind === "departure" ? (
              <TextField
                select
                label="Parent tour package"
                value={eventForm.parent_event_id}
                onChange={(e) => setEventForm({ ...eventForm, parent_event_id: e.target.value })}
                helperText="Each departure is a dated trip under one package catalog row."
              >
                <MenuItem value="">Select package</MenuItem>
                {packageEvents.map((pkg) => (
                  <MenuItem key={pkg.id} value={String(pkg.id)}>{pkg.title}</MenuItem>
                ))}
              </TextField>
            ) : null}
            <TextField
              label={
                eventForm.kind === "route"
                  ? "Route / trip name"
                  : eventForm.kind === "package"
                    ? "Package name"
                    : eventForm.kind === "departure"
                      ? "Departure label"
                      : "Event / class name"
              }
              value={eventForm.title}
              onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
              placeholder={
                eventForm.kind === "route"
                  ? "Chennai to Bangalore night bus"
                  : eventForm.kind === "package"
                    ? "Kerala 5D/4N — Munnar & Alleppey"
                    : eventForm.kind === "departure"
                      ? "Apr 2026 batch"
                      : "Weekend yoga workshop"
              }
              helperText="This name is shown to merchants and can be used in chatbot setup."
            />
            {eventForm.kind === "package" ? (
              <Stack spacing={1}>
                <TextField
                  label="Destination"
                  value={eventForm.destination}
                  onChange={(e) => setEventForm({ ...eventForm, destination: e.target.value })}
                  placeholder="Kerala, India"
                  fullWidth
                />
                <TextField
                  label="Inclusions (short)"
                  value={eventForm.inclusions}
                  onChange={(e) => setEventForm({ ...eventForm, inclusions: e.target.value })}
                  placeholder="Stay, breakfast, sightseeing, transfers"
                  multiline
                  minRows={2}
                  fullWidth
                />
                <TextField
                  select
                  label="Booking status"
                  value={eventForm.status}
                  onChange={(e) => setEventForm({ ...eventForm, status: e.target.value })}
                  fullWidth
                >
                  {statusOptions.map((s) => (
                    <MenuItem key={s} value={s}>{statusLabels[s] || s}</MenuItem>
                  ))}
                </TextField>
                <Alert severity="info">
                  Add <strong>departure</strong> rows for each travel date (set duration on each departure). Ticket types are configured on each departure, not on the package row.
                </Alert>
              </Stack>
            ) : null}
            {eventForm.kind === "route" ? (
              <Stack spacing={1}>
                <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
                  <TextField label="From" value={eventForm.origin} onChange={(e) => setEventForm({ ...eventForm, origin: e.target.value })} fullWidth />
                  <TextField label="To" value={eventForm.destination} onChange={(e) => setEventForm({ ...eventForm, destination: e.target.value })} fullWidth />
                  <TextField label="Bus / vehicle name" value={eventForm.vehicle_label} onChange={(e) => setEventForm({ ...eventForm, vehicle_label: e.target.value })} fullWidth />
                </Stack>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Stops and timings</Typography>
                {asArray(eventForm.route_stops).map((stop, idx) => (
                  <Stack key={`${idx}-${stop.name || "stop"}`} direction={{ xs: "column", md: "row" }} spacing={1}>
                    <TextField label="Stop name" value={stop.name || ""} onChange={(e) => updateRouteStop(idx, { name: e.target.value })} fullWidth />
                    <TextField type="datetime-local" label="Stop time" value={stop.time || ""} onChange={(e) => updateRouteStop(idx, { time: e.target.value })} InputLabelProps={{ shrink: true }} fullWidth />
                    <Button color="error" onClick={() => setEventForm({ ...eventForm, route_stops: asArray(eventForm.route_stops).filter((_, i) => i !== idx) })}>Remove</Button>
                  </Stack>
                ))}
                <Button variant="outlined" onClick={() => setEventForm({ ...eventForm, route_stops: [...asArray(eventForm.route_stops), { name: "", time: "" }] })}>
                  Add stop
                </Button>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Seat limits between stops</Typography>
                {asArray(eventForm.route_segments).map((segment, idx) => (
                  <Stack key={`${idx}-${segment.origin || "segment"}`} direction={{ xs: "column", md: "row" }} spacing={1}>
                    <TextField label="From stop" value={segment.origin || ""} onChange={(e) => updateRouteSegment(idx, { origin: e.target.value })} fullWidth />
                    <TextField label="To stop" value={segment.destination || ""} onChange={(e) => updateRouteSegment(idx, { destination: e.target.value })} fullWidth />
                    <TextField type="number" label="Seats available" value={segment.capacity || ""} onChange={(e) => updateRouteSegment(idx, { capacity: e.target.value })} fullWidth />
                    <Button color="error" onClick={() => setEventForm({ ...eventForm, route_segments: asArray(eventForm.route_segments).filter((_, i) => i !== idx) })}>Remove</Button>
                  </Stack>
                ))}
                <Button variant="outlined" onClick={() => setEventForm({ ...eventForm, route_segments: [...asArray(eventForm.route_segments), { origin: "", destination: "", capacity: "" }] })}>
                  Add segment
                </Button>
              </Stack>
            ) : eventForm.kind !== "package" ? (
              <TextField label="Place / venue" value={eventForm.venue_name} onChange={(e) => setEventForm({ ...eventForm, venue_name: e.target.value })} />
            ) : null}
            {eventForm.kind === "departure" ? (
              <Stack spacing={1.5} sx={{ p: 1.5, borderRadius: 2, border: "1px solid rgba(15,23,42,0.12)" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Optional hotels & vehicles (travel agency)</Typography>
                <Typography variant="body2" color="text.secondary">
                  Customers can skip these in WhatsApp. Prices are per person and multiply by traveller count.
                </Typography>
                <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
                  <TextField
                    type="number"
                    label="How many travellers can book this date?"
                    value={eventForm.travel_max_travellers}
                    onChange={(e) => setEventForm({ ...eventForm, travel_max_travellers: e.target.value })}
                    placeholder="e.g. 10"
                    helperText="Shown in WhatsApp as “X slots left” on each travel date (tour seats only)."
                    fullWidth
                  />
                  <TextField
                    type="number"
                    label="Base tour price per person (₹)"
                    value={eventForm.travel_base_price_inr}
                    onChange={(e) => setEventForm({ ...eventForm, travel_base_price_inr: e.target.value })}
                    fullWidth
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Optional hotel/vehicle types are add-ons — set their limits separately if needed. Do not use
                  “Ticket types” for trip capacity on travel departures; use the field above.
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Hotels (optional)</Typography>
                {asArray(eventForm.travel_hotels).map((row, idx) => (
                  <Stack key={`hotel-${idx}`} direction={{ xs: "column", md: "row" }} spacing={1}>
                    <TextField label="Hotel name" value={row.name || ""} onChange={(e) => {
                      const next = [...asArray(eventForm.travel_hotels)];
                      next[idx] = { ...next[idx], name: e.target.value };
                      setEventForm({ ...eventForm, travel_hotels: next });
                    }} fullWidth />
                    <TextField type="number" label="₹ / person" value={row.price_inr || ""} onChange={(e) => {
                      const next = [...asArray(eventForm.travel_hotels)];
                      next[idx] = { ...next[idx], price_inr: e.target.value };
                      setEventForm({ ...eventForm, travel_hotels: next });
                    }} fullWidth />
                    <Button color="error" onClick={() => setEventForm({
                      ...eventForm,
                      travel_hotels: asArray(eventForm.travel_hotels).filter((_, i) => i !== idx),
                    })}>Remove</Button>
                  </Stack>
                ))}
                <Button variant="outlined" onClick={() => setEventForm({
                  ...eventForm,
                  travel_hotels: [...asArray(eventForm.travel_hotels), { name: "", price_inr: "" }],
                })}>Add hotel option</Button>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Vehicles (optional)</Typography>
                {asArray(eventForm.travel_vehicles).map((row, idx) => (
                  <Stack key={`vehicle-${idx}`} direction={{ xs: "column", md: "row" }} spacing={1}>
                    <TextField label="Vehicle name" value={row.name || ""} onChange={(e) => {
                      const next = [...asArray(eventForm.travel_vehicles)];
                      next[idx] = { ...next[idx], name: e.target.value };
                      setEventForm({ ...eventForm, travel_vehicles: next });
                    }} fullWidth />
                    <TextField type="number" label="₹ / person" value={row.price_inr || ""} onChange={(e) => {
                      const next = [...asArray(eventForm.travel_vehicles)];
                      next[idx] = { ...next[idx], price_inr: e.target.value };
                      setEventForm({ ...eventForm, travel_vehicles: next });
                    }} fullWidth />
                    <Button color="error" onClick={() => setEventForm({
                      ...eventForm,
                      travel_vehicles: asArray(eventForm.travel_vehicles).filter((_, i) => i !== idx),
                    })}>Remove</Button>
                  </Stack>
                ))}
                <Button variant="outlined" onClick={() => setEventForm({
                  ...eventForm,
                  travel_vehicles: [...asArray(eventForm.travel_vehicles), { name: "", price_inr: "" }],
                })}>Add vehicle option</Button>
              </Stack>
            ) : null}
            {eventForm.kind !== "package" ? (
            <>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
              <TextField type="datetime-local" label="Start date & time" value={eventForm.starts_at_utc} onChange={(e) => setEventForm({ ...eventForm, starts_at_utc: e.target.value })} InputLabelProps={{ shrink: true }} fullWidth />
              <TextField type="datetime-local" label="End date & time" value={eventForm.ends_at_utc} onChange={(e) => setEventForm({ ...eventForm, ends_at_utc: e.target.value })} InputLabelProps={{ shrink: true }} fullWidth />
            </Stack>
            {eventForm.kind === "departure" ? (
              <>
                <TextField
                  label="Duration label"
                  value={eventForm.duration_label}
                  onChange={(e) => setEventForm({ ...eventForm, duration_label: e.target.value })}
                  placeholder="5 days / 4 nights"
                  helperText="Shown on each date line in {{travel_dates_text}} in WhatsApp."
                  fullWidth
                />
                <TextField
                  label="Itinerary"
                  value={eventForm.itinerary_text}
                  onChange={(e) => setEventForm({ ...eventForm, itinerary_text: e.target.value })}
                  placeholder={"Day 1: Arrival…\nDay 2: Sightseeing…"}
                  helperText="Shown in WhatsApp via List itinerary → {{travel_itinerary_text}}."
                  multiline
                  minRows={4}
                  fullWidth
                />
              </>
            ) : null}
            <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
              <TextField label="Timezone" value={eventForm.event_timezone} onChange={(e) => setEventForm({ ...eventForm, event_timezone: e.target.value })} helperText="Usually Asia/Kolkata" fullWidth />
              <TextField select label="Booking status" value={eventForm.status} onChange={(e) => setEventForm({ ...eventForm, status: e.target.value })} fullWidth>
                {statusOptions.map((s) => <MenuItem key={s} value={s}>{statusLabels[s] || s}</MenuItem>)}
              </TextField>
            </Stack>
            </>
            ) : (
              <Alert severity="info" sx={{ mt: 1 }}>
                Package catalog rows use today as an internal reference date. Customer-facing dates come from departure rows.
              </Alert>
            )}
            {eventForm.id && (
              <TextField
                label="Reference name"
                value={eventForm.slug}
                onChange={(e) => setEventForm({ ...eventForm, slug: e.target.value })}
                helperText="Optional. Used internally for saved links and developer references."
              />
            )}
            {eventForm.kind !== "package" ? (
            <>
            <Alert severity="info">
              <strong>Shared bus seat map:</strong> enable when Couple, Solo, and Group tiers sell seats on the same physical vehicle.
              Define all seat labels once on the trip; each ticket type sets its own price and seats-per-booking.
            </Alert>
            <TextField
              select
              label="Share one seat map for all ticket types on this trip?"
              value={eventForm.shared_seat_pool ? "yes" : "no"}
              onChange={(e) => setEventForm({ ...eventForm, shared_seat_pool: e.target.value === "yes" })}
            >
              <MenuItem value="no">No — each ticket type has its own seat list</MenuItem>
              <MenuItem value="yes">Yes — one bus / hall map for this trip</MenuItem>
            </TextField>
            {eventForm.shared_seat_pool && (
              <TextField
                label="All seat labels on this trip (comma or new line)"
                multiline
                minRows={3}
                value={eventForm.shared_seat_labels}
                onChange={(e) => setEventForm({ ...eventForm, shared_seat_labels: e.target.value })}
                placeholder="A1, A2, A3, B1, B2"
                helperText="Couple + Solo + Group tiers will block the same seats across types."
              />
            )}
            </>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setEventDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveEvent}>{eventForm.id ? "Save changes" : "Create"}</Button>
        </DialogActions>
      </Dialog>

      {tab === "tiers" && (
        <SectionCard
          title="Ticket types and seats"
          subtitle={selectedEvent ? selectedEvent.title : "Select a trip or event"}
          action={(
            <Button
              variant="contained"
              onClick={() => {
                setTierForm({ ...emptyTier, event_id: selectedEventId });
                setTierDialogOpen(true);
              }}
              disabled={!selectedEventId || (selectedEvent && eventCatalogKind(selectedEvent) === "package")}
              sx={{ borderRadius: 2, whiteSpace: "nowrap" }}
            >
              New ticket type
            </Button>
          )}
          sx={{ minHeight: 360 }}
        >
          {selectedEvent && eventCatalogKind(selectedEvent) === "package" ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This row is a <strong>tour package catalog</strong>. Open a <strong>departure</strong> under it to add ticket types and prices.
            </Alert>
          ) : null}
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.25} sx={{ mb: 2 }}>
            <TextField
              label="Search ticket type"
              size="small"
              value={tierFilters.search}
              onChange={(e) => setTierFilters({ ...tierFilters, search: e.target.value })}
              sx={{ minWidth: { xs: "100%", md: 280 } }}
            />
            <TextField
              select
              label="Seat handling"
              size="small"
              value={tierFilters.seat_mode}
              onChange={(e) => setTierFilters({ ...tierFilters, seat_mode: e.target.value })}
              sx={{ minWidth: { xs: "100%", md: 230 } }}
            >
              <MenuItem value="">All seat types</MenuItem>
              {Object.entries(seatModeLabels).map(([mode, label]) => (
                <MenuItem key={mode} value={mode}>{label}</MenuItem>
              ))}
            </TextField>
            <Button
              variant="text"
              onClick={() => setTierFilters({ search: "", seat_mode: "" })}
              sx={{ alignSelf: { md: "center" } }}
            >
              Clear
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Showing {visibleTiers.length} of {filteredTiers.length} filtered records ({selectedTiers.length} total)
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(auto-fill, minmax(300px, 360px))",
              },
              justifyContent: "start",
              alignItems: "stretch",
              gap: 1.5,
              maxHeight: 620,
              overflowY: "auto",
              pr: 0.5,
            }}
          >
                {visibleTiers.map((tier) => {
                  const meta = parseJson(tier.metadata_json, {});
                  const travelAddon = meta.travel_addon || null;
                  const remaining = Math.max(Number(tier.capacity || 0) - Number(tier.sold_count || 0), 0);
                  return (
                    <Box
                      key={tier.id}
                      sx={{
                        p: 1.75,
                        borderRadius: 2.5,
                        border: "1px solid rgba(15,23,42,0.10)",
                        bgcolor: "#fff",
                        minHeight: 140,
                        transition: "0.16s ease",
                        "&:hover": {
                          borderColor: "rgba(25,118,210,0.35)",
                          boxShadow: "0 10px 24px rgba(15,23,42,0.08)",
                        },
                      }}
                    >
                      <Stack justifyContent="space-between" gap={1.5} sx={{ height: "100%" }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mb: 0.5 }}>
                            {travelAddon === "hotel" ? <Chip size="small" label="Hotel add-on" color="info" /> : null}
                            {travelAddon === "vehicle" ? <Chip size="small" label="Vehicle add-on" color="info" /> : null}
                            {travelAddon === "base" ? <Chip size="small" label="Tour base" color="success" /> : null}
                          </Stack>
                          <Typography sx={{ fontWeight: 800, color: "#111827" }}>{tier.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {seatModeLabels[meta.seat_map?.mode] || "Total capacity only"}
                            {Number(meta.seat_map?.seats_per_unit || 1) > 1
                              ? ` • ${meta.seat_map.seats_per_unit} seats per booking`
                              : ""}
                          </Typography>
                          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                            <Chip size="small" label={`${tier.capacity} total`} />
                            <Chip size="small" color="success" label={`${remaining} slots left`} />
                            <Chip size="small" label={`${tier.sold_count || 0} sold`} />
                            <Chip size="small" label={formatMoney(tier.currency, tier.unit_price_minor)} />
                          </Stack>
                        </Box>
                        <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="flex-end" flexWrap="wrap">
                          <Button size="small" onClick={() => checkTierAvailability(tier.id)}>Availability</Button>
                          <Button size="small" variant="contained" onClick={() => { setTierForm(tierToForm(tier, selectedEventId)); setTierDialogOpen(true); }}>Edit</Button>
                          <Button size="small" color="error" onClick={() => openDeleteTierDialog(tier)}>Delete</Button>
                        </Stack>
                      </Stack>
                    </Box>
                  );
                })}
                {!visibleTiers.length && (
                  <Box sx={{ p: 3, textAlign: "center", border: "1px dashed rgba(15,23,42,0.18)", borderRadius: 2.5 }}>
                    <Typography sx={{ fontWeight: 700 }}>No matching ticket types</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Adjust filters or use New ticket type to add fare classes, entry categories, or seat groups.
                    </Typography>
                  </Box>
                )}
          </Box>
          {tierPageCount > 1 && (
            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
              <Pagination
                count={tierPageCount}
                page={currentTierPage}
                onChange={(_, page) => setTierPage(page)}
                color="primary"
              />
            </Stack>
          )}
              {availability && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Remaining: {availability.remaining} / Capacity: {availability.capacity} / Held: {availability.held_qty} / Sold: {availability.sold_count}
                </Alert>
              )}
        </SectionCard>
      )}

      <Dialog open={tierDialogOpen} onClose={() => setTierDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {tierForm.id ? "Edit ticket type" : "Add ticket type"}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField select label="Trip or event" value={tierForm.event_id || selectedEventId} onChange={(e) => { setSelectedEventId(e.target.value); setTierForm({ ...tierForm, event_id: e.target.value }); }}>
              {events.map((event) => <MenuItem key={event.id} value={String(event.id)}>{event.title}</MenuItem>)}
            </TextField>
            <TextField
              label="Ticket type name"
              value={tierForm.name}
              onChange={(e) => setTierForm({ ...tierForm, name: e.target.value })}
              placeholder="Sleeper, VIP, Adult, Window seat"
              helperText="Customers can see or select this category in chat."
            />
            <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
              <TextField label="How many can be sold?" type="number" value={tierForm.capacity} onChange={(e) => setTierForm({ ...tierForm, capacity: e.target.value })} fullWidth />
              <TextField
                label="Price per ticket"
                type="number"
                value={Number(tierForm.unit_price_minor || 0) / 100}
                onChange={(e) => setTierForm({ ...tierForm, unit_price_minor: Math.round(Number(e.target.value || 0) * 100) })}
                helperText="Enter normal price, e.g. 499"
                fullWidth
              />
              <TextField label="Currency" value={tierForm.currency} onChange={(e) => setTierForm({ ...tierForm, currency: e.target.value.toUpperCase().slice(0, 3) })} fullWidth />
            </Stack>
            <TextField select label="How should seats be handled?" value={tierForm.seat_map_mode} onChange={(e) => setTierForm({ ...tierForm, seat_map_mode: e.target.value })}>
              <MenuItem value="general_admission">{seatModeLabels.general_admission}</MenuItem>
              <MenuItem value="sectioned">{seatModeLabels.sectioned}</MenuItem>
              <MenuItem value="assigned_seat">{seatModeLabels.assigned_seat}</MenuItem>
            </TextField>
            {tierForm.seat_map_mode === "assigned_seat" && (
              <>
                <TextField
                  label="Seats required per booking"
                  type="number"
                  inputProps={{ min: 1, max: 20, step: 1 }}
                  value={tierForm.seats_per_unit}
                  onChange={(e) => setTierForm({ ...tierForm, seats_per_unit: e.target.value })}
                  helperText="Couple = 2, Solo = 1, Group package = 1 (then set quantity in chatbot). Capacity counts bookings (qty), not people."
                />
                <TextField
                  label="Seat labels customers can choose"
                  multiline
                  minRows={3}
                  value={tierForm.seat_labels}
                  onChange={(e) => setTierForm({ ...tierForm, seat_labels: e.target.value })}
                  placeholder="A1, A2, A3, B1, B2"
                  helperText={
                    events.find((e) => String(e.id) === String(tierForm.event_id || selectedEventId))
                      && parseJson(events.find((e) => String(e.id) === String(tierForm.event_id || selectedEventId))?.metadata_json, {})?.seat_map?.shared_pool
                      ? "Leave empty when the trip uses a shared seat map (configured on the trip/event)."
                      : "Comma-separated or one per line. Shown in chatbot and validated on booking."
                  }
                />
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Button size="small" variant="outlined" onClick={() => setTierForm({ ...tierForm, seat_labels: Array.from({ length: 20 }, (_, idx) => `A${idx + 1}`).join(", ") })}>
                    Generate A1-A20
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => setTierForm({ ...tierForm, seat_labels: ["A", "B", "C", "D"].flatMap((row) => Array.from({ length: 10 }, (_, idx) => `${row}${idx + 1}`)).join(", ") })}>
                    Generate A-D / 10 seats
                  </Button>
                </Stack>
              </>
            )}
            {tierForm.seat_map_mode !== "assigned_seat" && (
              <Alert severity="info">
                Seat names are only needed when customers must choose exact seats. Otherwise,
                the system sells from the total quantity you entered above.
              </Alert>
            )}
            {tierForm.id && (
              <TextField
                label="Reference code"
                value={tierForm.code}
                onChange={(e) => setTierForm({ ...tierForm, code: e.target.value })}
                helperText="Optional. Used internally for reports and developer references."
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setTierDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveTier}>{tierForm.id ? "Save changes" : "Add ticket type"}</Button>
        </DialogActions>
      </Dialog>

      {tab === "bookings" && (
        <SectionCard title="Ticket / reservation bookings" subtitle="Issued and cancelled reservations created from WhatsApp-native flows.">
          <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mb: 2 }}>
            <TextField select label="Event / route" value={bookingFilters.event_id} onChange={(e) => setBookingFilters({ ...bookingFilters, event_id: e.target.value })} sx={{ minWidth: 220 }}>
              <MenuItem value="">All inventory</MenuItem>
              {events.map((event) => <MenuItem key={event.id} value={String(event.id)}>{event.title}</MenuItem>)}
            </TextField>
            <TextField select label="Status" value={bookingFilters.status} onChange={(e) => setBookingFilters({ ...bookingFilters, status: e.target.value })} sx={{ minWidth: 180 }}>
              <MenuItem value="">All statuses</MenuItem>
              {["issued", "pending_payment", "cancelled", "partially_refunded"].map((status) => <MenuItem key={status} value={status}>{status}</MenuItem>)}
            </TextField>
            <TextField label="Contact search" value={bookingFilters.contact} onChange={(e) => setBookingFilters({ ...bookingFilters, contact: e.target.value })} />
            <Button variant="outlined" onClick={exportBookingsCsv}>Export CSV</Button>
          </Stack>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Inventory</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredBookings.map((booking) => (
                <TableRow key={booking.id}>
                  <TableCell>{booking.id}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatBookingContact(booking)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {events.find((e) => Number(e.id) === Number(booking.event_id))?.title || booking.event_id}
                    </Typography>
                    {asArray(booking.lines).map((line) => {
                      const seatText = formatBookingLineSeats(line);
                      return (
                      <Stack key={line.id} direction={{ xs: "column", md: "row" }} spacing={0.5} alignItems={{ md: "center" }}>
                        <Typography variant="caption" color="text.secondary">
                          {line.qty} x {line.tier_name || tierNameById[String(line.ticket_tier_id)] || line.ticket_tier_id}
                          {seatText ? ` • Seats: ${seatText}` : ""}
                        </Typography>
                        {booking.status === "issued" && Number(line.qty || 0) > 0 && (
                          <Button size="small" onClick={() => setLineEdit({ booking_id: booking.id, line_id: line.id, new_qty: Math.max(0, Number(line.qty || 0) - 1) })}>
                            Reduce
                          </Button>
                        )}
                      </Stack>
                      );
                    })}
                    {asArray(parseJson(booking.passenger_manifest_json, [])).map((passenger, idx) => (
                      <Typography key={`${booking.id}-passenger-${idx}`} variant="caption" color="text.secondary" display="block">
                        Passenger {idx + 1}: {passenger.name || passenger.full_name || "Unnamed"} {passenger.phone ? `• ${passenger.phone}` : ""}
                      </Typography>
                    ))}
                  </TableCell>
                  <TableCell>{formatMoney(booking.currency, booking.total_minor)}</TableCell>
                  <TableCell><Chip size="small" label={booking.status} /></TableCell>
                  <TableCell align="right">
                    {booking.status !== "cancelled" && (
                      <Button size="small" color="error" onClick={() => cancelBooking(booking)}>Cancel</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!filteredBookings.length && <TableRow><TableCell colSpan={6}>No ticket bookings match the filters.</TableCell></TableRow>}
            </TableBody>
          </Table>
          {lineEdit.booking_id && (
            <Box sx={{ mt: 2, p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Reduce booking line quantity</Typography>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
                <TextField type="number" label="New quantity" value={lineEdit.new_qty} onChange={(e) => setLineEdit({ ...lineEdit, new_qty: e.target.value })} />
                <Button variant="contained" onClick={reduceBookingLine}>Apply</Button>
                <Button onClick={() => setLineEdit({ booking_id: null, line_id: null, new_qty: "" })}>Cancel</Button>
              </Stack>
            </Box>
          )}
        </SectionCard>
      )}

      {tab === "chatbot" && (
        <Stack spacing={2}>
          <Alert severity="info">
            This page explains how to connect the ticket inventory you created here with the visual chatbot builder.
            Merchants should configure the journey with Flow, Action, Router, Payment, and Message nodes. Raw API
            payloads are available only as developer reference.
          </Alert>
          <SectionCard
            title="Ticket chatbot setup guide"
            subtitle="Use this as a checklist when building the customer journey in the visual chatbot canvas."
          >
            <Grid container spacing={2}>
              {[
                ["1", "Ask trip details", "Use message or form nodes to collect route/date, passenger count, pickup/drop segment, and seat preference."],
                ["2", "Show available options", "Ticket: Check availability → Message with {{ticket_availability_text}}, {{ticket_available_seats_text}}, and/or {{ticket_seats_text}}."],
                ["3", "Reserve inventory", "Use Create Ticket Hold after customer selects tier, quantity, seats, and passenger details."],
                ["4", "Route outcomes", "Add Workflow Router with the Ticket hold template. Connect Success, Seat Already Taken, Sold Out, Hold Expired, and Missing Details."],
                ["5", "Collect payment", "On Success, attach payment and send WhatsApp native review_and_pay."],
                ["6", "Confirm booking", "After payment success, send confirmation with trip, passenger, segment, and seat details."],
              ].map(([step, title, body]) => (
                <Grid item xs={12} md={4} key={step}>
                  <Card variant="outlined" sx={{ height: "100%" }}>
                    <CardContent>
                      <Chip label={`Step ${step}`} size="small" sx={{ mb: 1 }} />
                      <Typography fontWeight={700}>{title}</Typography>
                      <Typography variant="body2" color="text.secondary">{body}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </SectionCard>

          <SectionCard
            title="Travel agency WhatsApp flow"
            subtitle="Destination → date → optional hotel & vehicle → travellers → names → reserve."
          >
            <Stack spacing={1}>
              <Typography variant="body2">
                1. <strong>Package</strong> = destination & inclusions. <strong>Departure</strong> = date, duration label, optional hotels/vehicles.
              </Typography>
              <Typography variant="body2">
                2. <strong>List travel dates</strong> → {"{{tour_package_details}}"} + {"{{travel_dates_text}}"} → Question <code>selected_travel_date</code>.
              </Typography>
              <Typography variant="body2">
                3. <strong>List itinerary</strong> → Message with {"{{travel_itinerary_text}}"} (plan for the date they picked).
              </Typography>
              <Typography variant="body2">
                4. <strong>Show hotels & vehicles</strong> → {"{{hotel_options_text}}"} / {"{{vehicle_options_text}}"} → <code>selected_hotel</code> / <code>selected_vehicle</code> (or <code>skip</code>).
              </Typography>
              <Typography variant="body2">
                5. Question <code>member_count</code> → run <strong>Show hotels & vehicles</strong> again or a Message with {"{{travel_price_summary}}"}.
              </Typography>
              <Typography variant="body2">
                6. Collect names → <strong>Reserve trip</strong> (travel bundle on) → payment → confirmation.
              </Typography>
            </Stack>
          </SectionCard>

          <SectionCard
            title="Couple / Solo / Group on one bus"
            subtitle="Use shared seat map + seats per booking so types do not double-book the same seat."
          >
            <Stack spacing={1}>
              <Typography variant="body2">
                1. On the <strong>trip/event</strong>, enable <strong>Share one seat map</strong> and list all seats (A1–D10).
              </Typography>
              <Typography variant="body2">
                2. Create tiers: <strong>Couple</strong> (assigned seats, seats per booking = 2, capacity = max couples),
                <strong> Solo</strong> (seats per booking = 1), <strong>Group</strong> (general admission or seats as needed).
              </Typography>
              <Typography variant="body2">
                3. In chatbot: menu → Check availability for chosen tier → Reserve with <strong>Fixed quantity = 1</strong> for couple
                (customer sends 2 seat names) or qty from question for multiple couples.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Wrong seat count (e.g. 3 seats on Couple) returns <em>Seat required</em> on the ticket hold router.
              </Typography>
            </Stack>
          </SectionCard>

          <SectionCard
            title="What merchant must configure"
            subtitle="There is no extra ticket inventory setup required in this Chatbot Data tab."
          >
            <Grid container spacing={2}>
              {[
                ["Events / Routes", "Create the trips, events, routes, dates, and segments customers can book."],
                ["Inventory Tiers", "Create the ticket types, prices, capacity, and seat map rules."],
                ["Visual Chatbot Builder", "Build the customer journey and add Action + Workflow Router + Payment nodes."],
              ].map(([title, body]) => (
                <Grid item xs={12} md={4} key={title}>
                  <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 1.5, height: "100%" }}>
                    <Typography fontWeight={700}>{title}</Typography>
                    <Typography variant="body2" color="text.secondary">{body}</Typography>
                  </Box>
                </Grid>
              ))}
              <Grid item xs={12}>
                <Alert severity="success">
                  The chatbot automatically uses the selected event/tier IDs at runtime. Merchants do not need
                  to build or paste JSON payloads on this page.
                </Alert>
              </Grid>
            </Grid>
          </SectionCard>

          <SectionCard title="Recommended Workflow Router outputs">
            <Grid container spacing={2}>
              {[
                ["Success", "Continue to payment and confirmation."],
                ["Seat Already Taken", "Ask customer to pick another seat."],
                ["Sold Out", "Show another tier, route, or date."],
                ["Hold Expired", "Restart reservation before payment."],
                ["Missing Details", "Ask for passenger, seat, segment, or quantity again."],
                ["Default / fallback", "Send a friendly retry or route to staff support."],
              ].map(([label, body]) => (
                <Grid item xs={12} md={4} key={label}>
                  <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 1.5, height: "100%" }}>
                    <Typography fontWeight={700}>{label}</Typography>
                    <Typography variant="body2" color="text.secondary">{body}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </SectionCard>

          <SectionCard title="Developer reference" subtitle="Only needed for custom integrations or debugging. Merchants can ignore this section.">
            <Button variant="outlined" onClick={() => setShowDeveloperChatbotRef((prev) => !prev)}>
              {showDeveloperChatbotRef ? "Hide developer details" : "Show developer details"}
            </Button>
            {showDeveloperChatbotRef && (
              <Stack spacing={2} sx={{ mt: 2 }}>
                <TextField fullWidth multiline minRows={8} label="ticket hold action payload" value={JSON.stringify(holdPayloadPreview, null, 2)} InputProps={{ readOnly: true }} />
                <Box>
                  <Typography fontWeight={700} sx={{ mb: 1 }}>Registered internal actions</Typography>
                  {registryActions.map((a) => (
                    <Box key={a.id} sx={{ py: 1 }}>
                      <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }}>
                        <Chip label={a.method} size="small" />
                        <Typography fontWeight={700}>{a.id}</Typography>
                        <Typography color="text.secondary">{a.path}</Typography>
                      </Stack>
                      <Divider sx={{ mt: 1 }} />
                    </Box>
                  ))}
                </Box>
              </Stack>
            )}
          </SectionCard>
        </Stack>
      )}

      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          if (!deleteLoading) closeDeleteDialog();
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{deleteDialogTitle}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{deleteDialogBody}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDelete}
            disabled={deleteLoading}
          >
            {deleteLoading ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {noticeNode}
    </Box>
  );
}
